import { usePatientQuotes } from "../../quotes/useQuotes";
import { inferRouterOutputs } from "@trpc/server/unstable-core-do-not-import";
import type { AppRouter } from "cadendar-trpc-server";
import { isPecOcrApplicable } from "cadendar-shared";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFilePdf,
  FaSave,
  FaSpinner,
  FaSyncAlt,
  FaTimesCircle,
} from "react-icons/fa";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { trpc } from "../../main/components/trpc.ts";
import { getPecPdfUrl } from "./pecPdfUrl.ts";
import PecCoverageReview from "./PecCoverageReview";
import PecDocumentQuoteLink from "./PecDocumentQuoteLink";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

type PecRouterOutput = inferRouterOutputs<AppRouter>["pec"];
type PatientRouterOutput = inferRouterOutputs<AppRouter>["patient"];
type QuotesRouterOutput = inferRouterOutputs<AppRouter>["quotes"];

type PecDocumentListItem = PecRouterOutput["listPecDocuments"][number];
type PecDocumentDetail = PecRouterOutput["getPecDocument"];
type PatientSearchResult = PatientRouterOutput["findByName"][number];
type QuoteSummary = QuotesRouterOutput["getPatientQuotes"][number];
type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>["promise"]>;

interface PecDocumentViewerProps {
  documentId: PecDocumentListItem["id"];
  patientId?: string | null;
  allowAssignment?: boolean;
  onAssigned?: (patientId: string) => void;
}

interface PecFormValues {
  totalPecEuros: string;
  refundMutualEuros: string;
  refundSsEuros: string;
  patientResponsibilityEuros: string;
  patientLastName: string;
  patientFirstName: string;
  patientBirthdate: string;
  pecDate: string;
  pecNumber: string;
  peremptionDate: string;
}

function formatMoneyCents(value: number | null) {
  if (value === null) {
    return "";
  }

  return (value / 100).toFixed(2).replace(".", ",");
}

function parseMoneyToCents(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { value: null, error: null };
  }

  const normalized = trimmed.replace(/\s+/g, "").replace(",", ".");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return {
      value: null,
      error: "Les montants doivent être des nombres positifs.",
    };
  }

  return { value: Math.round(parsed * 100), error: null };
}

function parseFrenchDate(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { value: null, error: null };
  }

  const parsed = DateTime.fromFormat(trimmed, "dd/MM/yyyy");
  if (!parsed.isValid) {
    return { value: null, error: `${label} doit être au format jj/mm/aaaa.` };
  }

  return { value: parsed.toFormat("dd/MM/yyyy"), error: null };
}

function toFormValues(detail: PecDocumentDetail): PecFormValues {
  return {
    totalPecEuros: formatMoneyCents(detail.ocrData.totalPecCents),
    refundMutualEuros: formatMoneyCents(detail.ocrData.refundMutualCents),
    refundSsEuros: formatMoneyCents(detail.ocrData.refundSsCents),
    patientResponsibilityEuros: formatMoneyCents(
      detail.ocrData.patientResponsibilityCents,
    ),
    patientLastName: detail.ocrData.patientLastName ?? "",
    patientFirstName: detail.ocrData.patientFirstName ?? "",
    patientBirthdate: detail.ocrData.patientBirthdate ?? "",
    pecDate: detail.ocrData.pecDate ?? "",
    pecNumber: detail.ocrData.pecNumber ?? "",
    peremptionDate: detail.ocrData.peremptionDate ?? "",
  };
}

function formatStatusLabel(status: PecDocumentDetail["ocrStatus"]) {
  switch (status) {
    case "pending":
      return "En attente d'OCR";
    case "processing":
      return "OCR en cours";
    case "completed":
      return "OCR terminé";
    case "failed":
      return "OCR échoué";
    case "not_applicable":
      return "OCR non requis";
  }
}

function formatDocumentType(documentType: PecDocumentDetail["documentType"]) {
  if (documentType === "coverage_request") {
    return "Demande de prise en charge";
  }
  if (documentType === "coverage_response") {
    return "Prise en charge";
  }
  return "Type non classé";
}

function formatCapturedAt(value: string) {
  return DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm");
}

const PecDocumentViewer = ({
  documentId,
  patientId = null,
  allowAssignment = false,
  onAssigned,
}: PecDocumentViewerProps) => {
  const utils = trpc.useUtils();
  const detailQuery = trpc.pec.getPecDocument.useQuery(documentId);
  const updateMutation = trpc.pec.updatePecDocumentFields.useMutation({
    onSuccess: async () => {
      await utils.pec.getPecDocument.invalidate(documentId);
      await utils.pec.listPecDocuments.invalidate();
      setFormDraft(null);
      setFeedbackMessage("Champs PEC enregistrés.");
    },
    onError: (error) => {
      setErrorMessage(error.message || "Erreur lors de l'enregistrement.");
    },
  });
  const reanalyzeMutation = trpc.pec.reanalyzePecDocument.useMutation({
    onSuccess: async () => {
      await utils.pec.getPecDocument.invalidate(documentId);
      await utils.pec.listPecDocuments.invalidate();
      setFeedbackMessage("L'OCR PEC a été relancé.");
    },
    onError: (error) => {
      setErrorMessage(error.message || "Impossible de relancer l'OCR.");
    },
  });
  const assignMutation = trpc.pec.assignPecDocument.useMutation({
    onSuccess: async (assignedDocument) => {
      await utils.pec.getPecDocument.invalidate(documentId);
      await utils.pec.listPecDocuments.invalidate();
      if (assignedDocument.patientId) {
        await utils.patientMedia.listByPatient.invalidate({
          patientId: assignedDocument.patientId,
          mediaKind: "document",
        });
        onAssigned?.(assignedDocument.patientId);
      }
      setFeedbackMessage("La PEC a été rattachée au patient.");
    },
    onError: (error) => {
      setErrorMessage(error.message || "Impossible de rattacher la PEC.");
    },
  });

  const [formDraft, setFormDraft] = useState<{
    documentId: string;
    values: PecFormValues;
  } | null>(null);
  const formValues =
    formDraft?.documentId === documentId
      ? formDraft.values
      : detailQuery.data
        ? toFormValues(detailQuery.data)
        : null;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] =
    useState<PatientSearchResult | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>("");
  const [documentTypeDraft, setSelectedDocumentType] = useState<
    "coverage_request" | "coverage_response" | "" | null
  >(null);
  const selectedDocumentType =
    documentTypeDraft ?? detailQuery.data?.documentType ?? "coverage_response";
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfDocument, setPdfDocument] = useState<PdfDocument | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<{
    cancel: () => void;
    promise: Promise<unknown>;
  } | null>(null);

  const patientSearchQuery = trpc.patient.findByName.useQuery(
    { nameLookup: searchTerm },
    { enabled: allowAssignment && searchTerm.trim().length > 2 },
  );
  const patientQuotesQuery = usePatientQuotes(selectedPatient?.id);
  const pdfUrl = getPecPdfUrl(documentId);
  const documentAvailable = !!detailQuery.data;

  useEffect(() => {
    const status = detailQuery.data?.ocrStatus;
    if (status !== "pending" && status !== "processing") {
      return;
    }

    const timer = window.setInterval(() => {
      void detailQuery.refetch();
    }, 3000);

    return () => {
      window.clearInterval(timer);
    };
  }, [detailQuery, detailQuery.data?.ocrStatus]);

  useEffect(() => {
    if (!allowAssignment || selectedPatient || searchTerm.trim().length > 0) {
      return;
    }

    const lastName = detailQuery.data?.ocrData.patientLastName?.trim();
    const firstName = detailQuery.data?.ocrData.patientFirstName?.trim();
    if (!lastName && !firstName) {
      return;
    }

    setSearchTerm([lastName, firstName].filter(Boolean).join(" "));
  }, [allowAssignment, detailQuery.data, searchTerm, selectedPatient]);

  useEffect(() => {
    if (!documentAvailable) {
      setPdfDocument(null);
      setTotalPages(1);
      setIsPdfLoading(false);
      setPdfLoadError(null);
      return;
    }

    let cancelled = false;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;

    setPdfDocument(null);
    setTotalPages(1);
    setIsPdfLoading(true);
    setPdfLoadError(null);

    void (async () => {
      try {
        const response = await fetch(pdfUrl, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Impossible de charger le PDF.");
        }

        const pdfBytes = new Uint8Array(await response.arrayBuffer());
        if (cancelled) {
          return;
        }

        loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
        const loadedPdf = await loadingTask.promise;
        if (cancelled) {
          void loadedPdf.destroy();
          return;
        }

        setPdfDocument(loadedPdf);
        setTotalPages(loadedPdf.numPages);
        setCurrentPage(1);
      } catch (error: unknown) {
        if (!cancelled) {
          setPdfLoadError(
            error instanceof Error
              ? error.message
              : "Impossible de charger le PDF.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsPdfLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
      renderTaskRef.current?.cancel();
    };
  }, [documentAvailable, pdfUrl]);

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current) {
      return;
    }

    let cancelled = false;

    void pdfDocument
      .getPage(currentPage)
      .then((page) => {
        if (cancelled || !canvasRef.current) {
          return;
        }

        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (!context) {
          return;
        }

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderTaskRef.current?.cancel();
        const task = page.render({
          canvas,
          canvasContext: context,
          viewport,
        });
        renderTaskRef.current = task;

        return task.promise.catch((error: unknown) => {
          if (
            error instanceof Error &&
            error.name === "RenderingCancelledException"
          ) {
            return;
          }
          throw error;
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Impossible d'afficher la page PDF.",
          );
        }
      });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [pdfDocument, currentPage]);

  if (detailQuery.error || (!detailQuery.isPending && !detailQuery.data)) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {detailQuery.error?.message || "Impossible de charger cette PEC."}
      </div>
    );
  }

  if (!detailQuery.data || !formValues) {
    return (
      <div
        role="status"
        className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500"
      >
        Chargement de la PEC...
      </div>
    );
  }

  const detail = detailQuery.data;

  const saveFields = () => {
    setErrorMessage(null);
    setFeedbackMessage(null);

    const totalPec = parseMoneyToCents(formValues.totalPecEuros);
    const refundMutual = parseMoneyToCents(formValues.refundMutualEuros);
    const refundSs = parseMoneyToCents(formValues.refundSsEuros);
    const patientResponsibility = parseMoneyToCents(
      formValues.patientResponsibilityEuros,
    );
    const patientBirthdate = parseFrenchDate(
      formValues.patientBirthdate,
      "La date de naissance",
    );
    const pecDate = parseFrenchDate(formValues.pecDate, "La date de PEC");
    const peremptionDate = parseFrenchDate(
      formValues.peremptionDate,
      "La date de péremption",
    );

    const fieldErrors = [
      totalPec.error,
      refundMutual.error,
      refundSs.error,
      patientResponsibility.error,
      patientBirthdate.error,
      pecDate.error,
      peremptionDate.error,
    ].filter(Boolean);

    if (fieldErrors.length > 0) {
      setErrorMessage(fieldErrors[0] ?? "Les champs PEC sont invalides.");
      return;
    }

    updateMutation.mutate({
      id: detail.id,
      fields: {
        totalPecCents: totalPec.value,
        refundMutualCents: refundMutual.value,
        refundSsCents: refundSs.value,
        patientResponsibilityCents: patientResponsibility.value,
        patientLastName: formValues.patientLastName.trim() || null,
        patientFirstName: formValues.patientFirstName.trim() || null,
        patientBirthdate: patientBirthdate.value,
        pecDate: pecDate.value,
        pecNumber: formValues.pecNumber.trim() || null,
        peremptionDate: peremptionDate.value,
      },
    });
  };

  const assignToPatient = () => {
    if (!selectedPatient) {
      setErrorMessage("Sélectionne un patient avant de rattacher la PEC.");
      return;
    }

    setErrorMessage(null);
    setFeedbackMessage(null);
    assignMutation.mutate({
      id: detail.id,
      patientId: selectedPatient.id,
      quoteId: selectedQuoteId || null,
      documentType: selectedDocumentType || null,
    });
  };

  const statusClassName =
    detail.ocrStatus === "completed"
      ? "border-green-200 bg-green-50 text-green-800"
      : detail.ocrStatus === "failed"
        ? "border-red-200 bg-red-50 text-red-800"
        : detail.ocrStatus === "not_applicable"
          ? "border-slate-200 bg-slate-50 text-slate-700"
          : "border-blue-200 bg-blue-50 text-blue-800";

  const fieldRows = [
    {
      key: "totalPecEuros",
      label: "Total PEC",
      placeholder: "0,00",
    },
    {
      key: "refundMutualEuros",
      label: "Remb. mutuelle (RC)",
      placeholder: "0,00",
    },
    {
      key: "refundSsEuros",
      label: "Remb. Sécurité sociale (RO)",
      placeholder: "0,00",
    },
    {
      key: "patientResponsibilityEuros",
      label: "Reste à charge",
      placeholder: "0,00",
    },
    {
      key: "patientLastName",
      label: "Nom",
      placeholder: "Nom du patient",
    },
    {
      key: "patientFirstName",
      label: "Prénom",
      placeholder: "Prénom du patient",
    },
    {
      key: "patientBirthdate",
      label: "Date de naissance",
      placeholder: "jj/mm/aaaa",
    },
    {
      key: "pecDate",
      label: "Date PEC",
      placeholder: "jj/mm/aaaa",
    },
    {
      key: "pecNumber",
      label: "N° PEC",
      placeholder: "Numéro de PEC",
    },
    {
      key: "peremptionDate",
      label: "Date péremption",
      placeholder: "jj/mm/aaaa",
    },
  ] as const;

  return (
    <div>
      <PecDocumentQuoteLink key={`quote-${detail.id}`} document={detail} />
      <PecCoverageReview key={`review-${detail.id}`} document={detail} />
      <div className="flex flex-col gap-6 xl:flex-row">
        <div className="flex-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FaFilePdf className="text-red-500" />
                {formatDocumentType(detail.documentType)}
              </div>
              <p className="text-sm text-slate-500">
                {detail.network} · capturé le{" "}
                {formatCapturedAt(detail.capturedAt)}
              </p>
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200"
            >
              <FaExternalLinkAlt size={12} />
              Ouvrir le PDF
            </a>
          </div>

          <div className="mb-3 flex items-center justify-center gap-4 rounded-lg bg-slate-100 p-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Précédent
            </button>
            <span className="text-sm font-medium text-slate-700">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={currentPage === totalPages}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Suivant
            </button>
          </div>

          <div className="overflow-hidden rounded-lg bg-slate-900 shadow-inner">
            {isPdfLoading ? (
              <div className="flex h-80 items-center justify-center text-sm text-slate-300">
                Chargement du PDF...
              </div>
            ) : pdfLoadError ? (
              <div className="flex h-80 items-center justify-center px-6 text-sm text-red-300">
                {pdfLoadError}
              </div>
            ) : (
              <canvas ref={canvasRef} className="w-full h-auto" />
            )}
          </div>
        </div>

        <div className="w-full xl:w-[26rem] xl:shrink-0">
          <div className="sticky top-4 flex flex-col gap-4">
            <div className={`rounded-xl border p-4 ${statusClassName}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">
                    {formatStatusLabel(detail.ocrStatus)}
                  </p>
                  {detail.ocrStatus === "failed" && detail.ocrError ? (
                    <p className="mt-1 text-sm">{detail.ocrError}</p>
                  ) : detail.ocrStatus === "not_applicable" ? (
                    <p className="mt-1 text-sm">
                      Ce document n&apos;est pas une prise en charge effective.
                    </p>
                  ) : detail.ocrStatus !== "completed" ? (
                    <p className="mt-1 text-sm">
                      Les champs seront mis à jour automatiquement dès que l'OCR
                      se termine.
                    </p>
                  ) : null}
                </div>
                {detail.ocrStatus === "pending" ||
                detail.ocrStatus === "processing" ? (
                  <FaSpinner className="animate-spin" />
                ) : detail.ocrStatus === "completed" ||
                  detail.ocrStatus === "not_applicable" ? (
                  <FaCheckCircle />
                ) : (
                  <FaExclamationTriangle />
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900">
                  Champs PEC
                </h3>
                <div className="flex items-center gap-2">
                  {detail.ocrStatus === "failed" &&
                  isPecOcrApplicable(detail.documentType) ? (
                    <button
                      type="button"
                      onClick={() => {
                        setErrorMessage(null);
                        setFeedbackMessage(null);
                        reanalyzeMutation.mutate({ id: detail.id });
                      }}
                      disabled={reanalyzeMutation.isPending}
                      className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60"
                    >
                      <FaSyncAlt size={12} />
                      Re-analyser
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={saveFields}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                  >
                    <FaSave size={12} />
                    Enregistrer
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {fieldRows.map(({ key, label, placeholder }) => {
                  const value = formValues[key];

                  return (
                    <div
                      key={key}
                      className="flex items-center gap-2 rounded-md p-2 text-sm text-slate-700"
                    >
                      <div
                        className={`h-3 w-3 shrink-0 rounded-full ${
                          value ? "bg-green-500" : "bg-slate-300"
                        }`}
                      />
                      <span className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {label}
                      </span>
                      <div className="flex flex-1 items-center border-b border-slate-300 transition-colors focus-within:border-blue-500">
                        <input
                          type="text"
                          aria-label={label}
                          disabled={updateMutation.isPending}
                          value={value}
                          onChange={(event) => {
                            setFeedbackMessage(null);
                            setErrorMessage(null);
                            setFormDraft((previous) => ({
                              documentId,
                              values: {
                                ...(previous?.documentId === documentId
                                  ? previous.values
                                  : toFormValues(detail)),
                                [key]: event.target.value,
                              },
                            }));
                          }}
                          className="min-w-0 flex-1 bg-transparent py-1 text-sm text-slate-900 outline-none"
                          placeholder={placeholder}
                        />
                        {value ? (
                          <button
                            type="button"
                            onClick={() =>
                              setFormDraft((previous) => ({
                                documentId,
                                values: {
                                  ...(previous?.documentId === documentId
                                    ? previous.values
                                    : toFormValues(detail)),
                                  [key]: "",
                                },
                              }))
                            }
                            disabled={updateMutation.isPending}
                            className="p-1 text-slate-400 transition-colors hover:text-red-500"
                            title="Effacer"
                          >
                            <FaTimesCircle />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              {feedbackMessage ? (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {feedbackMessage}
                </div>
              ) : null}
              {errorMessage ? (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errorMessage}
                </div>
              ) : null}
            </div>

            {allowAssignment && detail.patientId === null ? (
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-lg font-semibold text-slate-900">
                  Rattacher au patient
                </h3>
                {!selectedPatient ? (
                  <div className="relative">
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Rechercher un patient
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                      value={searchTerm}
                      onChange={(event) => {
                        setSelectedPatient(null);
                        setSelectedQuoteId("");
                        setSearchTerm(event.target.value);
                      }}
                      placeholder="Nom ou prénom"
                    />
                    {patientSearchQuery.data &&
                    patientSearchQuery.data.length > 0 ? (
                      <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                        {patientSearchQuery.data.map((candidate) => (
                          <button
                            key={candidate.id}
                            type="button"
                            className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm transition-colors last:border-b-0 hover:bg-blue-50"
                            onClick={() => {
                              setSelectedPatient(candidate);
                              setSearchTerm("");
                            }}
                          >
                            <span className="font-semibold text-slate-900">
                              {candidate.name} {candidate.forename}
                            </span>
                            <span className="ml-2 text-xs text-slate-500">
                              {candidate.birthdate}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold">
                          {selectedPatient.name} {selectedPatient.forename}
                        </p>
                        <p className="text-xs text-green-700">
                          {selectedPatient.birthdate}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-xs font-medium text-green-800 underline"
                        onClick={() => {
                          setSelectedPatient(null);
                          setSelectedQuoteId("");
                        }}
                      >
                        Changer
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-4 space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Type de document
                    </label>
                    <select
                      value={selectedDocumentType}
                      onChange={(event) =>
                        setSelectedDocumentType(
                          event.target.value as
                            | "coverage_request"
                            | "coverage_response"
                            | "",
                        )
                      }
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    >
                      <option value="coverage_response">Prise en charge</option>
                      <option value="coverage_request">
                        Demande de prise en charge
                      </option>
                      <option value="">Non classé</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Devis associé
                    </label>
                    <select
                      value={selectedQuoteId}
                      onChange={(event) =>
                        setSelectedQuoteId(event.target.value)
                      }
                      disabled={
                        !selectedPatient || patientQuotesQuery.isLoading
                      }
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"
                    >
                      <option value="">Aucun devis</option>
                      {(patientQuotesQuery.data ?? []).map(
                        (quote: QuoteSummary) => (
                          <option key={quote.id} value={quote.id}>
                            {quote.name} ·{" "}
                            {DateTime.fromJSDate(quote.creation_date).toFormat(
                              "dd/MM/yyyy",
                            )}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={assignToPatient}
                    disabled={!selectedPatient || assignMutation.isPending}
                    className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {assignMutation.isPending
                      ? "Rattachement..."
                      : "Rattacher au patient"}
                  </button>
                </div>
              </div>
            ) : null}

            {patientId ? (
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
                Patient courant :{" "}
                <span className="font-semibold text-slate-700">
                  {patientId}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PecDocumentViewer;
